<script lang="ts">
  import { debounce } from 'lodash-es';
  import { onMount } from 'svelte';
  import { m } from '../../../paraglide/messages.js';
  import { tippyTooltip } from '../../tippy';

  export type ProrationPreview = {
    planStartDate: string;
    planStartDateFormatted: string;
    proratedAmountLabel: string;
    proratedAmount?: string;
    proratedAmountTaxes?: string;
    billingStartsAtFormatted: string;
    labels: {
      planStartDate: string;
      membershipFeesDue: string;
    };
  };

  type Props = {
    previewUrl: string;
    form: HTMLFormElement | null;
  };

  let { previewUrl, form }: Props = $props();

  let preview = $state<ProrationPreview | null>(null);
  let loading = $state(false);

  const DATE_INPUT_SELECTOR =
    'input[name="membership_application[requested_starts_at]"]';
  const EXTRAS_SELECTOR = 'input[name*="[plan_extras]"][name*="[chosen]"]';
  const DISCOUNT_CODE_PARAM_KEY = 'membership_application[discount_code]';
  const DISCOUNT_CODE_SELECTOR = `[name="${DISCOUNT_CODE_PARAM_KEY}"]`;

  const PREVIEW_PARAM_KEYS = [
    /membership_application\[plan_id\]/,
    /membership_application\[requested_starts_at\]/,
    /membership_application\[discount_code\]/,
    /membership_application\[plan_extras\]/,
  ];

  const dateInput = $derived(
    form?.querySelector<HTMLInputElement>(DATE_INPUT_SELECTOR) ?? null,
  );

  // Only the fields that affect the proration preview are sent
  function isRelevantParam(key: string): boolean {
    return PREVIEW_PARAM_KEYS.some((pattern) => pattern.test(key));
  }

  // The discount code field is rendered as a hidden input once a discount has been applied
  // Typing a not-yet-submitted code doesn't affect the preview.
  function isDiscountCodeApplied(currentForm: HTMLFormElement): boolean {
    const discountCodeInput = currentForm.querySelector<HTMLInputElement>(
      DISCOUNT_CODE_SELECTOR,
    );
    return discountCodeInput?.type === 'hidden';
  }

  async function loadPreview() {
    if (!form) {
      return;
    }

    // When requested-start-date field is present, wait until the member picks a date.
    // Without one (members_cant_request_start_at) use the server-supplied default.
    if (dateInput && !dateInput.value) {
      return;
    }

    loading = true;
    try {
      const discountCodeApplied = isDiscountCodeApplied(form);
      const url = new URL(previewUrl, window.location.origin);
      const searchParams = new URLSearchParams();
      for (const [key, value] of new FormData(form).entries()) {
        if (key === DISCOUNT_CODE_PARAM_KEY && !discountCodeApplied) {
          continue;
        }
        if (isRelevantParam(key) && typeof value === 'string') {
          searchParams.append(key, value);
        }
      }
      url.search = searchParams.toString();

      try {
        const response = await fetch(url.toString(), {
          headers: { Accept: 'application/json' },
        });
        preview = response.ok ? await response.json() : null;
      } catch {
        preview = null;
      }
    } finally {
      loading = false;
    }
  }

  const fetchPreview = debounce(loadPreview, 300);

  onMount(() => {
    if (!form) {
      return;
    }

    const extraInputs =
      form.querySelectorAll<HTMLInputElement>(EXTRAS_SELECTOR);

    const handleChange = () => {
      fetchPreview();
    };

    dateInput?.addEventListener('change', handleChange);
    dateInput?.addEventListener('input', handleChange);
    extraInputs.forEach((extraInput) => {
      extraInput.addEventListener('change', handleChange);
    });

    // Always fetch from the current form state so browser-restored fields
    // (date, extras) are reflected in the first render.
    void loadPreview();

    return () => {
      dateInput?.removeEventListener('change', handleChange);
      dateInput?.removeEventListener('input', handleChange);
      extraInputs.forEach((extraInput) => {
        extraInput.removeEventListener('change', handleChange);
      });
      fetchPreview.cancel();
    };
  });
</script>

{#if preview}
  <div data-testid="membership-signup-proration">
    <p>
      <span class="admin-label">{preview.labels.planStartDate}:</span>
      <span data-testid="plan-start-date">{preview.planStartDateFormatted}</span
      >
    </p>
    {#if preview.proratedAmount}
      <p>
        <span class="admin-label">{preview.proratedAmountLabel}:</span>
        {#if preview.proratedAmountTaxes}
          <span
            data-testid="prorated-amount"
            {@attach tippyTooltip(preview.proratedAmountTaxes)}
          >
            {preview.proratedAmount}
          </span>
        {:else}
          <span data-testid="prorated-amount">{preview.proratedAmount}</span>
        {/if}
      </p>
    {/if}
    <p>
      <span class="admin-label">{preview.labels.membershipFeesDue}:</span>
      <span data-testid="billing-starts-at"
        >{preview.billingStartsAtFormatted}</span
      >
    </p>
    {#if loading}
      <span class="visually-hidden" aria-live="polite"
        >{m['shared.updating']()}</span
      >
    {/if}
  </div>
{/if}
